/**
 * NavMesh — client-side navigation graph + pathfinding (ENG-592).
 *
 * Consumes the backend navmesh JSON (ENG-591, lightmap-baking-service --navmesh):
 *   { version, splatUrl, bounds:{min,max}, floorY, cellSize,
 *     nodes:[{i,p:[x,y,z]}], edges:[{a,b,cost}] }
 *
 * Pure logic, no three / no DOM — so it unit-tests under node:test. Distances
 * are computed on the XZ plane (the graph is a single floor layer).
 */

function dist(ax, az, bx, bz) {
  const dx = ax - bx, dz = az - bz
  return Math.sqrt(dx * dx + dz * dz)
}

// Minimal binary min-heap keyed by f-score, storing node indices.
class MinHeap {
  constructor() { this.items = [] } // [{ node, f }]
  get size() { return this.items.length }
  push(node, f) {
    const a = this.items
    a.push({ node, f })
    let i = a.length - 1
    while (i > 0) {
      const p = (i - 1) >> 1
      if (a[p].f <= a[i].f) break
      ;[a[p], a[i]] = [a[i], a[p]]
      i = p
    }
  }
  pop() {
    const a = this.items
    const top = a[0]
    const last = a.pop()
    if (a.length > 0) {
      a[0] = last
      let i = 0
      for (;;) {
        const l = 2 * i + 1, r = 2 * i + 2
        let s = i
        if (l < a.length && a[l].f < a[s].f) s = l
        if (r < a.length && a[r].f < a[s].f) s = r
        if (s === i) break
        ;[a[s], a[i]] = [a[i], a[s]]
        i = s
      }
    }
    return top
  }
}

export class NavMesh {
  constructor(data) {
    if (!data || !Array.isArray(data.nodes) || !Array.isArray(data.edges)) {
      throw new Error('NavMesh: invalid navmesh data (need nodes[] + edges[])')
    }
    this.version = data.version ?? 1
    this.splatUrl = data.splatUrl ?? ''
    this.bounds = data.bounds ?? null
    this.floorY = Number.isFinite(data.floorY) ? data.floorY : 0
    this.cellSize = data.cellSize > 0 ? data.cellSize : 0.25
    this.nodes = data.nodes // [{ i, p:[x,y,z] }]
    this.boxes = Array.isArray(data.boxes) ? data.boxes : [] // [{ min:[x,y,z], max:[x,y,z] }] collision boxes

    // Adjacency (undirected).
    this.adj = this.nodes.map(() => [])
    for (const e of data.edges) {
      if (e.a == null || e.b == null) continue
      const cost = Number.isFinite(e.cost) ? e.cost : this._edgeCost(e.a, e.b)
      this.adj[e.a].push({ to: e.b, cost })
      this.adj[e.b].push({ to: e.a, cost })
    }

    // Uniform-grid spatial index for nearest-node lookup.
    this._buckets = new Map()
    for (const n of this.nodes) {
      const key = this._bucketKey(n.p[0], n.p[2])
      let arr = this._buckets.get(key)
      if (!arr) { arr = []; this._buckets.set(key, arr) }
      arr.push(n.i)
    }
  }

  get nodeCount() { return this.nodes.length }

  _edgeCost(a, b) {
    const pa = this.nodes[a].p, pb = this.nodes[b].p
    return dist(pa[0], pa[2], pb[0], pb[2])
  }

  _bucketKey(x, z) {
    return Math.floor(x / this.cellSize) + ',' + Math.floor(z / this.cellSize)
  }

  /** Nearest walkable node to a world point (x,y,z or {x,z}); null if empty. */
  nearestNode(point) {
    if (this.nodes.length === 0) return null
    const x = Array.isArray(point) ? point[0] : point.x
    const z = Array.isArray(point) ? point[2] : point.z
    const gx = Math.floor(x / this.cellSize)
    const gz = Math.floor(z / this.cellSize)

    let best = -1, bestD = Infinity
    const consider = (idx) => {
      const p = this.nodes[idx].p
      const d = dist(x, z, p[0], p[2])
      if (d < bestD) { bestD = d; best = idx }
    }
    // Expand square rings until we find nodes; then scan one extra ring so a
    // closer node in an adjacent bucket can't be missed.
    const maxRing = 256
    let foundRing = -1
    for (let r = 0; r <= maxRing; r++) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue // ring only
          const arr = this._buckets.get((gx + dx) + ',' + (gz + dz))
          if (arr) for (const idx of arr) consider(idx)
        }
      }
      if (best >= 0 && foundRing < 0) foundRing = r
      if (foundRing >= 0 && r >= foundRing + 1) break
    }
    return best
  }

  /**
   * A* from world `start` to world `goal`. Returns an array of [x,y,z]
   * waypoints (node centers, goal node last), or null if unreachable / empty.
   */
  findPath(start, goal) {
    const startNode = this.nearestNode(start)
    const goalNode = this.nearestNode(goal)
    if (startNode == null || goalNode == null || startNode < 0 || goalNode < 0) return null
    if (startNode === goalNode) return [this.nodes[goalNode].p.slice()]

    const gp = this.nodes[goalNode].p
    const h = (idx) => {
      const p = this.nodes[idx].p
      return dist(p[0], p[2], gp[0], gp[2])
    }
    const n = this.nodes.length
    const gScore = new Float64Array(n).fill(Infinity)
    const cameFrom = new Int32Array(n).fill(-1)
    const closed = new Uint8Array(n)
    gScore[startNode] = 0
    const open = new MinHeap()
    open.push(startNode, h(startNode))

    while (open.size > 0) {
      const { node: cur } = open.pop()
      if (cur === goalNode) break
      if (closed[cur]) continue
      closed[cur] = 1
      for (const { to, cost } of this.adj[cur]) {
        if (closed[to]) continue
        const tentative = gScore[cur] + cost
        if (tentative < gScore[to]) {
          gScore[to] = tentative
          cameFrom[to] = cur
          open.push(to, tentative + h(to))
        }
      }
    }
    if (cameFrom[goalNode] < 0 && goalNode !== startNode) return null

    // Reconstruct.
    const idxPath = []
    for (let c = goalNode; c !== -1; c = cameFrom[c]) {
      idxPath.push(c)
      if (c === startNode) break
    }
    idxPath.reverse()
    if (idxPath[0] !== startNode) return null
    return idxPath.map(i => this.nodes[i].p.slice())
  }

  /**
   * A random node reachable from `center` within `radius` (XZ), UE
   * GetRandomReachablePointInRadius analog. Returns [x,y,z] or null.
   * `rng` lets tests inject determinism (defaults to Math.random).
   */
  getRandomReachablePoint(center, radius, rng = Math.random) {
    const startNode = this.nearestNode(center)
    if (startNode == null || startNode < 0) return null
    const cx = Array.isArray(center) ? center[0] : center.x
    const cz = Array.isArray(center) ? center[2] : center.z

    // BFS over the connected component, keeping nodes within radius.
    const seen = new Uint8Array(this.nodes.length)
    const within = []
    const queue = [startNode]
    seen[startNode] = 1
    while (queue.length) {
      const cur = queue.shift()
      const p = this.nodes[cur].p
      if (dist(cx, cz, p[0], p[2]) <= radius) within.push(cur)
      for (const { to } of this.adj[cur]) {
        if (!seen[to]) {
          const tp = this.nodes[to].p
          // Only traverse outward through nodes near the radius so we don't
          // walk the whole map for a large component.
          if (dist(cx, cz, tp[0], tp[2]) <= radius + this.cellSize) {
            seen[to] = 1
            queue.push(to)
          }
        }
      }
    }
    if (within.length === 0) return null
    const pick = within[Math.floor(rng() * within.length) % within.length]
    return this.nodes[pick].p.slice()
  }
}
